import React, { useState } from "react";
import {
  AdminApiClient,
  AdminApiContext,
  gregAdminTheme,
  MicrositeEditor as BaseEditor,
} from "@getregistered/greg-editors";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorHandler from "./ErrorHandler";
import ChakraProviderWithNonce from "./ChakraProviderWithNonce";

import railsFetch, { fetchJson } from "./railsFetch";

const mapSectionsToRailsParams = (sections, deletedSections) => {
  const railsSections = sections.map((section, index) => {
    const railsSection = { ...section, position: index + 1 };
    if (railsSection.isNew) {
      delete railsSection.isNew;
      delete railsSection.id;
    }
    if (railsSection.image) {
      railsSection.linked_image_id = railsSection.image.id;
      delete railsSection.image;
    } else {
      railsSection.linked_image_id = null;
      railsSection.image = null;
    }
    if (railsSection.inlineImage) {
      railsSection.linked_inline_image_id = railsSection.inlineImage.id;
      railsSection.inline_image_title = railsSection.inlineImage.title;
      delete railsSection.inlineImage;
    } else {
      railsSection.linked_inline_image_id = null;
      railsSection.inline_image = null;
    }
    if (railsSection.download) {
      railsSection.linked_file_id = railsSection.download.id;
      railsSection.file_title = railsSection.download.title;
      delete railsSection.download;
    } else {
      railsSection.linked_file_id = null;
      railsSection.file_title = null;
      railsSection.file = null;
    }
    if (railsSection.sponsorTierId) {
      railsSection.sponsorship_sponsor_tier_id = railsSection.sponsorTierId;
    }
    delete railsSection.sponsorTierId;
    if (railsSection.trackIds) {
      railsSection.track_ids = railsSection.trackIds;
    }
    delete railsSection.trackIds;
    if (railsSection.deviceVisibility !== undefined) {
      railsSection.device_visibility = railsSection.deviceVisibility;
    }
    delete railsSection.deviceVisibility;
    return railsSection;
  });
  if (deletedSections) {
    deletedSections.forEach((id) => railsSections.push({ id, _destroy: true }));
  }

  return railsSections;
};

const mapPagesToRailsParams = (pages, deletedPages) => {
  const railsPages = pages.map((page, index) => {
    const railsPage = {
      ...page,
      track_ids: page.trackIds,
      position: index + 1,
    };
    if (railsPage.sections || railsPage.deletedSections) {
      railsPage.microsite_sections_attributes = mapSectionsToRailsParams(
        railsPage.sections || [],
        railsPage.deletedSections
      );
    }
    delete railsPage.trackIds;
    delete railsPage.sections;
    delete railsPage.deletedSections;
    if (railsPage.deviceVisibility !== undefined) {
      railsPage.device_visibility = railsPage.deviceVisibility;
    }
    delete railsPage.deviceVisibility;
    // Coalesce undefined → null so a cleared picker reaches Rails as an
    // explicit nil. JSON.stringify drops undefined keys; without this, the
    // Clear button silently no-ops on save.
    railsPage.scheduled_at = railsPage.scheduledAt ?? null;
    delete railsPage.scheduledAt;
    if (railsPage.isNew) {
      delete railsPage.isNew;
      delete railsPage.id;
    }
    return railsPage;
  });
  if (deletedPages) {
    deletedPages.forEach((id) => railsPages.push({ id, _destroy: true }));
  }
  return railsPages;
};

export default function MicrositeEditor({
  pages,
  eventPaths = undefined,
  tracks = undefined,
  apiBaseUrl,
  saveUrl,
  fileUploadUrl,
  sponsorshipEnabled,
  richTextEnabled = false,
  markdownSectionsEnabled = false,
  agendaBuilderEnabled = false,
  mobileAppEnabled = false,
  scheduledPagesEnabled = false,
  timezone,
}) {
  const [loading, setLoading] = useState(false);
  const [initialPages, setPages] = useState(pages);
  const apiClient = new AdminApiClient(apiBaseUrl, railsFetch);
  const handleSave = async (newPages, deletedPages) => {
    const mappedPages = mapPagesToRailsParams(newPages, deletedPages);
    setLoading(true);
    const response = await fetchJson(saveUrl, {
      method: "PUT",
      body: JSON.stringify({
        event: {
          microsite_pages_attributes: mappedPages,
        },
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      const data = await response.json();
      setPages(data.pages);
    } else if (response.status === 401) {
      window.alert("Session expired. Please login to continue");
      window.open(response.url);
    } else {
      window.alert(`Error saving form: status ${response.status}`);
    }
  };

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("file_upload[file]", file, file.name);
    const response = await railsFetch(fileUploadUrl, {
      method: "POST",
      body: formData,
    });
    // GR-1893: server now returns 422 on disallowed types / oversize uploads.
    // Without this check the error JSON would be dispatched into the section's
    // image / download property and silently break it.
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = await response.json();
        const messages = Array.from(
          new Set(Object.values(body).flat().filter((m) => typeof m === "string"))
        );
        if (messages.length > 0) message = messages.join(". ");
      } catch (_) {
        // fall through with the HTTP status message
      }
      window.alert(`Upload failed: ${message}`);
      throw new Error(`Upload failed (${response.status}): ${message}`);
    }
    return await response.json();
  };

  return (
    <ErrorHandler>
      <ChakraProviderWithNonce theme={gregAdminTheme}>
        <AdminApiContext.Provider value={apiClient}>
          <QueryClientProvider client={new QueryClient()} contextSharing={true}>
            <BaseEditor
              initialPages={initialPages}
              eventPaths={eventPaths}
              tracks={tracks}
              onSave={handleSave}
              onFileUpload={handleFileUpload}
              sponsorshipEnabled={sponsorshipEnabled}
              useRichText={richTextEnabled}
              markdownSectionsEnabled={markdownSectionsEnabled}
              agendaBuilderEnabled={agendaBuilderEnabled}
              mobileAppEnabled={mobileAppEnabled}
              scheduledPagesEnabled={scheduledPagesEnabled}
              timezone={timezone}
            />
          </QueryClientProvider>
        </AdminApiContext.Provider>
      </ChakraProviderWithNonce>
    </ErrorHandler>
  );
}
